import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { label: "Features", to: "/features" },
  { label: "Solutions", to: "/solutions" },
  { label: "Pricing", to: "/pricing" },
  { label: "How It Works", to: "/#how-it-works" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { status } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(to.slice(2));
        el?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    } else {
      navigate(to);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-slate-200 bg-white/85 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
      data-testid="public-header"
    >
      <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
        <Link to="/" data-testid="header-logo-link">
          <Logo variant={scrolled ? "dark" : "light"} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => go(n.to)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${
                scrolled
                  ? `hover:bg-slate-100 hover:text-slate-900 ${pathname === n.to ? "text-blue-800" : "text-slate-600"}`
                  : `hover:bg-white/10 hover:text-white ${pathname === n.to ? "text-white" : "text-slate-200"}`
              }`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-2.5 lg:flex">
          <Button
            variant="ghost"
            onClick={() => navigate(status === "authed" ? "/portal/dashboard" : "/login")}
            data-testid="header-login-btn"
            className={`font-semibold ${scrolled ? "text-slate-700 hover:bg-slate-100 hover:text-slate-900" : "text-white hover:bg-white/10 hover:text-white"}`}
          >
            {status === "authed" ? "Open Portal" : "Login"}
          </Button>
          <Button
            onClick={() => navigate("/request-demo")}
            data-testid="header-request-demo-btn"
            className="group gap-1.5 rounded-full bg-blue-800 px-5 font-semibold text-white hover:bg-blue-900"
          >
            Request Demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </div>

        <button
          className={`rounded-lg p-2 lg:hidden ${scrolled || open ? "text-slate-800" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.label}
                onClick={() => go(n.to)}
                className="rounded-lg px-3 py-2.5 text-left text-base font-semibold text-slate-700 hover:bg-slate-100"
              >
                {n.label}
              </button>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              <Button variant="outline" onClick={() => go(status === "authed" ? "/portal/dashboard" : "/login")} data-testid="mobile-login-btn">
                {status === "authed" ? "Open Portal" : "Login"}
              </Button>
              <Button onClick={() => go("/request-demo")} className="bg-blue-800 text-white hover:bg-blue-900">
                Request Demo
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
